<template>
  <Loading v-if="loading" />
  <div class="deliveryHistory">
    <div class="deliveryHistory__header">
      <div class="deliveryHistory__header-dropdowns">
        <Dropdown
          class="deliveryHistory__header-dropdowns-dropdown"
          :selectedOption="selectedReservations.selectedOption"
          :options="selectedReservations.options"
          :multiple="true"
          @selectOption="selectReservations"
          data-testid="selectReservations"
        />
        <Dropdown
          class="deliveryHistory__header-dropdowns-dropdown"
          :selectedOption="getOrderStatus.selectedOption"
          :options="getOrderStatus.options"
          @selectOption="changeOrderStatus"
          data-testid="changeOrderStatus"
        />
      </div>
      <div class="deliveryHistory__header-dateAndSearch">
        <div class="date">
          <div class="date-inputs">
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="firstDay"
                name="startTime"
                :message="$t('errorDate')"
                @change="changeDateStartTime"
                data-testid="startTime"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="today"
                name="endTime"
                @change="changeDateEndTime"
                data-testid="endTime"
              />
            </div>
          </div>
        </div>
        <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
        <div class="input-block">
          <img
            class="dropdown-img"
            src="@/assets/icons/Search.svg"
            alt="Search"
          />
          <input
            v-model="search"
            class="input-search"
            :placeholder="$t('search')"
            @input="changeInput"
          />
          <Dropdown
            className="dropdown-search"
            :selectedOption="getSearchBy.selectedOption"
            :options="getSearchBy.options"
            @selectOption="changeSearchBy"
          />
        </div>
      </div>
    </div>
    <div v-if="deliveryHistoryList.length">
      <Table
        :data="deliveryHistoryList"
        :columns="columns"
        :idActiveDelivery="idActiveDelivery"
        selectedField="deliveryId"
        @openQRCode="openQRCode"
        @addDescription="addDescription"
        @goToItem="infoReservation"
      />
    </div>
    <div
      class="deliveryHistory__noReserve"
      v-if="!isDateCorrect || !deliveryHistoryList.length"
    >
      <p>{{ $t('noData') }}</p>
    </div>
  </div>
  <AddDescription
    :showModal="showModal"
    :boxId="boxId"
    :activeDelivery="activeDelivery"
    @updateList="updateList"
    @closeModal="showModal = false"
  />
  <OpenQRCode
    :showQRCode="showQRCode"
    :idActiveDelivery="idActiveDelivery"
    @closeModal="showQRCode = false"
  />
  <PickedUpDeleteReservation
    :showModalDelete="showModalDelete"
    :idDelivery="idDelivery"
    @pickedUpdeletedOrRemovedRequest="pickedUpdeletedOrRemovedRequest"
    @closeModal="showModalDelete = false"
  />
  <InfoReservation
    :showModal="showModalReservation"
    @closeModal="showModalReservation = false"
    :activeDelivery="activeDelivery"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import AddDescription from './AddDescription/AddDescription.vue';
import OpenQRCode from '@/duplicatePages/OpenQRCode/OpenQRCode.vue';
import PickedUpDeleteReservation from './PickedUpDeleteReservation/PickedUpDeleteReservation.vue';
import InfoReservation from './InfoReservation/InfoReservation.vue';

import { searchByOptions, searchActions, columns } from './configs';
import { InputFileEvent, Option } from '@/types';
import {
  DataReservations,
  Delivery,
  DeliveryDefaultParams,
  PickupBox,
  Reservation
} from '@/store/modules/pharmacy/types';

import { format } from 'date-fns';

@Options({
  name: 'DeliveryHistory',
  emits: ['pickedUpdeletedOrRemovedRequest'],
  components: {
    AddDescription,
    OpenQRCode,
    PickedUpDeleteReservation,
    Loading,
    InfoReservation
  },
  data() {
    return {
      selectedReservations: {
        selectedOption: [],
        options: {}
      },
      getOrderStatus: {
        selectedOption: {
          value: 'all',
          label: this.$t('allUpperCase')
        },
        options: searchByOptions(this.$t)
      },
      getSearchBy: {
        selectedOption: {
          value: 'phoneNumber',
          label: this.$t('mobilePhone')
        },
        options: searchActions(this.$t)
      },
      search: '',
      activeDelivery: null,
      idActiveDelivery: '',
      activePickupBox: null,
      activePickupBoxes: null,
      columns: columns(this.$t),
      showModal: false,
      showModalReservation: false,
      showQRCode: false,
      showModalDelete: false,
      idDelivery: '',
      dataPickedUpRequest: {},
      loading: false,
      params: null,
      deliveryHistoryList: {},
      deliveryHistoryData: {},
      defaultParams: null,
      boxId: '',
      updateListRequest: false,
      showDecodedSsn: '',
      changeDateParams: {
        target: {
          name: '',
          value: ''
        }
      }
    };
  },
  created() {
    this.loadPickupBoxes();
  },
  computed: {
    ...mapState('pharmacy', ['pharmacy', 'pickupBoxes', 'deliveryHistory']),
    today() {
      const dateString = format(new Date(), 'dd/MM/yyyy');

      return dateString;
    },
    firstDay() {
      const firstDate = format(new Date(), 'dd/MM/yyyy').split('/');
      firstDate.splice(0, 1, '01');

      return firstDate.join('/');
    },
    isDateCorrect() {
      if (this.isDatePresent) {
        return this.params.endTime >= this.params.startTime;
      }

      return true;
    },
    isDatePresent() {
      return this.params && this.params.startTime && this.params.endTime;
    }
  },
  updated() {
    if (this.pickupBoxes) {
      this.selectedReservations.selectedOption =
        this.pickupBoxAndPharmacyOptions;
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPickupBoxesByPharmacy',
      'getDeliveryHistory',
      'deliveryDeletedOrRemovedPickup'
    ]),
    async loadPickupBoxes() {
      this.loading = true;

      const { id } = this.$route.params;
      await this.getPickupBoxesByPharmacy(id);

      this.loading = false;

      this.initPickupBoxOptions();
      this.initRequest();
    },
    initPickupBoxOptions() {
      this.pickupBoxOptions = this.pickupBoxes.map((pickupBox: PickupBox) => ({
        value: pickupBox.info.id,
        label: pickupBox.info.name,
        img: require('@/assets/icons/PickupBox.svg')
      }));

      const { id } = this.$route.params;

      const pharmacyReservations = {
        value: id,
        label: this.$t('pharmacyReservations')
      };

      this.selectedReservations.options = {
        pharmacyReservations,
        ...this.pickupBoxOptions
      };

      this.pickupBoxAndPharmacyOptions = [
        ...this.pickupBoxOptions,
        pharmacyReservations
      ];

      this.selectedReservations.selectedOption =
        this.pickupBoxAndPharmacyOptions;
    },
    initRequest() {
      let startTime = this.firstDay.split('/');

      startTime = new Date(
        +startTime[2],
        startTime[1] - 1,
        +startTime[0]
      ).valueOf();

      this.params = {
        startTime: startTime + new Date(startTime).getTimezoneOffset() * -60000,
        endTime: new Date().valueOf()
      };
    },
    show() {
      const mapBox = this.pickupBoxAndPharmacyOptions.map(
        (i: PickupBox) => i.id || i.value
      );

      mapBox.forEach((value: string) => {
        this.defaultParams = {
          boxId_or_pharmacyId: value,
          ...this.params
        };

        this.getDeliveryHistoryList(this.defaultParams);
      });
    },
    async getDeliveryHistoryList(defaultParams: DeliveryDefaultParams) {
      if (
        defaultParams.boxId_or_pharmacyId &&
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        this.loading = true;

        await this.getDeliveryHistory(this.defaultParams);

        this.deliveryHistoryData = this.deliveryHistory;

        this.deliveryHistoryList = Object.values(
          this.deliveryHistoryData
        ).flat();

        this.deliveryHistoryList.sort((a: Delivery, b: Delivery) => {
          return b.reservedTime - a.reservedTime;
        });

        this.loading = false;
      }
    },
    changeDateStartTime(e: InputFileEvent) {
      const { name, value } = e.target;

      this.changeDateParams.name = name;
      this.changeDateParams.value = value;

      const dateNumbers = value.split('/');
      const date = `${dateNumbers[1]}/${dateNumbers[0]}/${dateNumbers[2]}`;

      this.params = {
        ...this.params,
        [name]:
          new Date(date).valueOf() + new Date(date).getTimezoneOffset() * -60000
      };
    },
    changeDateEndTime(e: InputFileEvent) {
      const { name, value } = e.target;

      this.changeDateParams.name = name;
      this.changeDateParams.value = value;

      const dateNumbers = value.split('/');

      const date = new Date(
        +dateNumbers[2],
        +dateNumbers[1] - 1,
        +dateNumbers[0],
        23,
        59,
        59
      );
      const timestamp = date.getTime();

      this.params = {
        ...this.params,
        [name]: timestamp - 1000
      };
    },
    selectReservations(option: readonly Option[]) {
      this.selectedReservations.selectedOption = option;

      const selectedReservations = this.selectedReservations.selectedOption.map(
        (option: Option) => option.value
      );

      const filteredHistory = Object.fromEntries(
        Object.entries(this.deliveryHistoryData).filter(([key]) =>
          selectedReservations.includes(key)
        )
      );

      this.deliveryHistoryList = Object.values(filteredHistory).flat();

      this.deliveryHistoryList.sort((a: Delivery, b: Delivery) => {
        return b.reservedTime - a.reservedTime;
      });
    },
    changeOrderStatus(option: readonly Option[]) {
      this.getOrderStatus.selectedOption = option;

      this.deliveryHistoryList = [
        ...(Object.values(this.deliveryHistory) as DataReservations[]).flat()
      ].filter(
        (item: DataReservations) =>
          item.deliveryStatus === this.getOrderStatus.selectedOption.value
      );

      if (this.getOrderStatus.selectedOption.value === 'all') {
        this.show();
      }
    },
    changeInput(e: InputFileEvent) {
      const { value } = e.target;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        if (value === '') {
          this.deliveryHistoryList = Object.values(
            this.deliveryHistoryData
          ).flat();

          this.deliveryHistoryList.sort((a: Delivery, b: Delivery) => {
            return b.reservedTime - a.reservedTime;
          });
        } else if (this.getSearchBy.selectedOption.value === 'phoneNumber') {
          this.deliveryHistoryList = [
            ...(
              Object.values(this.deliveryHistory) as DataReservations[]
            ).flat()
          ].filter((item: DataReservations) =>
            item.phoneNumber.toLowerCase().includes(value?.toLowerCase())
          );
        } else if (this.getSearchBy.selectedOption.value === 'deliveryId') {
          this.deliveryHistoryList = [
            ...(
              Object.values(this.deliveryHistory) as DataReservations[]
            ).flat()
          ].filter((item: DataReservations) =>
            item.deliveryId.toLowerCase().includes(value?.toLowerCase())
          );
        }
      }, 600);
    },
    changeSearchBy(option: readonly Option[]) {
      this.getSearchBy.selectedOption = option;

      this.deliveryHistoryList = [
        ...(Object.values(this.deliveryHistory) as DataReservations[]).flat()
      ].filter((item: DataReservations) =>
        (
          item[
            this.getSearchBy.selectedOption.value as keyof DataReservations
          ] as string
        )
          ?.toLowerCase()
          .includes(this.search.toLowerCase())
      );
    },
    async pickedUpdeletedOrRemovedRequest(deliveryId: string) {
      const { id } = this.$route.params;

      const payload = {
        pharmacyId: id,
        deliveryId: deliveryId,
        pickupboxId: this.dataPickedUpRequest.pickupboxId,
        reservedTime: this.dataPickedUpRequest.reservedTime
      };

      await this.deliveryDeletedOrRemovedPickup(payload);

      this.showModalDelete = false;

      this.show();
      this.loadPickupBoxes();
    },
    addDescription(id: string) {
      const deliveryHistoryWhitIdBox = Object.entries(this.deliveryHistory)
        .map(([boxId, reservations]: [string, unknown]) =>
          (reservations as Delivery[]).map((reservation: Delivery) => ({
            ...reservation,
            boxId: boxId
          }))
        )
        .flat();

      this.activeDelivery = deliveryHistoryWhitIdBox.find(
        (i: Delivery) => i.deliveryId === id
      );

      this.activePickupBox = this.pickupBoxes.find((pickupBox: PickupBox) =>
        pickupBox.reservations
          .map((i: Reservation) => i.deliveryId)
          .includes(this.activeDelivery.deliveryId)
      );

      this.boxId = this.activeDelivery.boxId;

      this.showModal = true;
    },
    openQRCode(id: string) {
      this.activeDelivery = [
        ...(Object.values(this.deliveryHistoryList) as DataReservations[])
      ].find((i: DataReservations) => i.deliveryId === id);

      this.idActiveDelivery = this.activeDelivery.deliveryId;

      this.showQRCode = true;
    },
    updateList() {
      this.updateListRequest = !this.updateListRequest;
    },
    infoReservation(deliveryId: string) {
      this.activeDelivery = this.deliveryHistoryList.find(
        (i: Delivery) => i.deliveryId === deliveryId
      );
      this.showModalReservation = true;
    }
  },
  watch: {
    'selectedReservations.selectedOption': {
      immediate: true,
      handler(option: (Option | string)[]) {
        this.pickupBoxAndPharmacyOptions = option;
      }
    },
    params() {
      if (this.isDatePresent && this.isDateCorrect) {
        this.initPickupBoxOptions();
        this.show(this.params);
      }
    },
    updateListRequest() {
      this.loadPickupBoxes();
    }
  }
})
export default class DeliveryHistory extends Vue {}
</script>

<style lang="scss">
.deliveryHistory {
  padding: 24px;
  .deliveryHistory__header {
    display: flex;
    justify-content: space-between;
    .deliveryHistory__header-dropdowns {
      .deliveryHistory__header-dropdowns-dropdown {
        height: 40px;
        width: 288px !important;
        margin-right: 10px;
        margin-bottom: 10px;
      }
      .deliveryHistory__header-dropdowns-notActivePickupBox {
        padding-bottom: 36px;
      }
    }
    .deliveryHistory__header-dateAndSearch {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      .date {
        display: inline-block;
        height: 42px;
        margin-bottom: 8px;
        .date-inputs {
          display: flex;
          align-items: center;
          height: 100%;
          .input {
            margin-bottom: 0;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
      }
      .date-error {
        color: $red;
        font-size: $fourteenSize;
        padding: 2px 0 10px;
        margin-right: 64%;
      }
      .input-block {
        display: flex;
        position: relative;
        margin-left: 10px;
        .input-search {
          width: 245px;
          height: 38px;
          padding-left: 36px;
          font-size: $fourteenSize;
          border: 1px solid $border;
          border-right: 0;
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          outline: none;
        }
        .dropdown-img {
          position: absolute;
          top: 14px;
          left: 17px;
        }
        .dropdown {
          max-width: 150px;
          .dropdown-search {
            height: 40px;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        }
      }
    }
  }
  .deliveryHistory__noReserve {
    margin-top: 24px;
  }
}
</style>
